import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Image } from '@inter-ikea-kompis/component-image';
import { Text } from '@inter-ikea-kompis/component-text';

import * as SkapaStyle from '@ingka/common-styles-webc';

import SummaryPageSecurityStyle from './SummaryPageSecurityStyle.js';

/**
 * The view for the Summary Page Security.
 */

export default class SummaryPageSecurity extends KompisElement {
	public static eventTypes = [];

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// The text for the title
	@property({ type: String })
	// eslint-disable-next-line lit/no-native-attributes
	public override title = '';

	// The text for the description
	@property({ type: String })
	public description: string | null = null;

	// The src string for the image
	@property({ type: String })
	public imageSrc: string | null = null;

	// The alt text for the image
	@property({ type: String })
	public imageAlt: string | null = null;

	// Highest heading in the summary page security tab.
	@property({ type: Number })
	public startHeadingsAtLevel: 4 | 5 = 4;

	public override render(): RenderResult {
		return html`
			<style>
				${SummaryPageSecurityStyle.getStyle({ theme: this.theme })}
			</style>
			<div>${this.getTitle()} ${this.getDescription()} ${this.getSecurityImage()}</div>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-image', Image);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	private getTitle(): RenderResult {
		if (!this.title) {
			return null;
		}

		const tagName = `h${this.startHeadingsAtLevel}`;
		const element = document.createElement(tagName);
		element.className = `no-margin title ${SkapaStyle.Text.classnames({ heading: { m: true } })}`;

		element.innerText = this.title;

		return element;
	}

	private getDescription(): RenderResult {
		if (!this.description) {
			return null;
		}

		return html` <kompis-text
			class="description"
			.theme="${this.theme}"
			.type="${ThemeFontStyleTypeEnum.bodyLarge}"
			>${this.description}</kompis-text
		>`;
	}

	private getSecurityImage(): RenderResult {
		if (!this.imageSrc || !this.imageAlt) {
			return null;
		}

		return html`<kompis-image .alt="${this.imageAlt}" .src="${this.imageSrc}"></kompis-image>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-summary-page-security': SummaryPageSecurity;
	}
}
